import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '@relay-towing/tokens';
import { focusRingOffset, uiMarker } from '../recipes/shared';

export interface SliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  /** Accessible name, when there is no visible <Field> label above it. */
  'aria-label'?: string;
}

/**
 * A value picked from a continuous range.
 *
 * Renders one thumb per value, so a range slider is just `value={[min, max]}`.
 * Keyboard support (arrows, Home/End, PageUp/PageDown) comes from Radix.
 *
 * Worth knowing: a slider is a poor way to enter a precise number — pair it
 * with an Input when the exact value matters, and use it alone only when the
 * user is choosing a feel rather than a figure.
 */
export const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(function Slider({ className, ...props }, ref) {
  const thumbs = props.value ?? props.defaultValue ?? [0];

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        uiMarker,
        'relative flex w-full touch-none select-none items-center',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        className
      )}
      {...props}
    >
      <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-pill bg-muted">
        <SliderPrimitive.Range className="absolute h-full bg-primary" />
      </SliderPrimitive.Track>
      {thumbs.map((_, i) => (
        <SliderPrimitive.Thumb
          key={i}
          className={cn(
            'block size-4 rounded-pill border-2 border-primary bg-background shadow-e1',
            'transition-colors duration-fast hover:bg-muted',
            focusRingOffset
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
});
